import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  LogOut,
  Factory,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  Cog,
  Zap,
  Shield,
  Activity,
  Brain,
  Target,
  Wrench,
  Thermometer,
  Gauge,
  Settings
} from 'lucide-react'
import InfoTooltip from '../common/InfoTooltip'

export default function ManufactureraDashboard({ onBack, onLogout, businessData }) {
  const kpis = [
    {
      title: 'Eficiencia Producción',
      value: '87%',
      change: '+15%',
      trend: 'up',
      icon: Factory,
      color: 'blue',
      tooltip: {
        title: 'Optimización de Eficiencia en Producción',
        description: 'Porcentaje de eficiencia en líneas de producción optimizada mediante IA para maximizar throughput y minimizar desperdicios.',
        aiMethod: 'Análisis de flujo de producción, optimización de secuencias, predicción de cuellos de botella, ML para mejora continua.',
        dataSource: 'Datos de producción en tiempo real, sensores IoT, histórico de eficiencia, tiempos de ciclo.',
        updateFrequency: 'Monitoreo continuo con optimización en tiempo real'
      }
    },
    {
      title: 'Reducción Downtime',
      value: '55%',
      change: '+23%',
      trend: 'up',
      icon: Clock,
      color: 'green',
      description: 'Menos paradas',
      tooltip: {
        title: 'Reducción de Tiempo de Inactividad',
        description: 'Disminución del tiempo de parada de máquinas mediante mantenimiento predictivo y prevención de fallas.',
        aiMethod: 'Análisis predictivo de fallas, mantenimiento basado en condición, alertas tempranas de deterioro.',
        dataSource: 'Sensores de vibración, temperatura, presión, histórico de mantenimientos, patrones de falla.',
        updateFrequency: 'Monitoreo continuo con predicciones actualizadas diariamente'
      }
    },
    {
      title: 'Calidad Productos',
      value: '99.1%',
      change: '+2.3%',
      trend: 'up',
      icon: Shield,
      color: 'purple',
      tooltip: {
        title: 'Control de Calidad Inteligente',
        description: 'Porcentaje de productos que cumplen estándares de calidad mediante inspección automatizada con IA.',
        aiMethod: 'Computer vision para inspección visual, ML para detección de defectos, control estadístico de procesos.',
        dataSource: 'Imágenes de productos, mediciones de calidad, histórico de defectos, especificaciones técnicas.',
        updateFrequency: 'Inspección continua con reportes de calidad en tiempo real'
      }
    },
    {
      title: 'Ahorro Energético',
      value: '32%',
      change: '+8%',
      trend: 'up',
      icon: Zap,
      color: 'orange',
      description: 'Consumo reducido',
      tooltip: {
        title: 'Optimización Energética Inteligente',
        description: 'Reducción en el consumo energético mediante optimización inteligente de procesos y equipos.',
        aiMethod: 'Optimización de cargas energéticas, predicción de demanda, control automático de equipos.',
        dataSource: 'Medidores de energía, patrones de consumo, programación de producción, tarifas eléctricas.',
        updateFrequency: 'Optimización continua con ajustes automáticos'
      }
    }
  ]

  const predictiveMaintenance = [
    {
      machine: 'Línea Ensamble A1',
      health: 78,
      nextMaintenance: '3 días',
      predictedFailure: 'Rodamientos desgastados',
      criticalLevel: 'medium',
      efficiency: 92,
      color: 'yellow'
    },
    {
      machine: 'Prensa Hidráulica B2',
      health: 45,
      nextMaintenance: 'Inmediato',
      predictedFailure: 'Sobrecalentamiento sistema',
      criticalLevel: 'high',
      efficiency: 67,
      color: 'red'
    },
    {
      machine: 'Robot Soldadura C3',
      health: 94,
      nextMaintenance: '12 días',
      predictedFailure: 'Desgaste normal',
      criticalLevel: 'low',
      efficiency: 98,
      color: 'green'
    },
    {
      machine: 'Transportador D4',
      health: 82,
      nextMaintenance: '7 días',
      predictedFailure: 'Banda transportadora',
      criticalLevel: 'medium',
      efficiency: 89,
      color: 'yellow'
    }
  ]

  const productionMetrics = [
    {
      line: 'Línea A',
      target: 1000,
      actual: 1087,
      efficiency: 108.7,
      quality: 99.2,
      downtime: 2.3,
      aiOptimization: 'Incrementar velocidad 5%'
    },
    {
      line: 'Línea B',
      target: 800,
      actual: 756,
      efficiency: 94.5,
      quality: 98.8,
      downtime: 8.2,
      aiOptimization: 'Revisar alimentador automático'
    },
    {
      line: 'Línea C',
      target: 1200,
      actual: 1234,
      efficiency: 102.8,
      quality: 99.4,
      downtime: 1.8,
      aiOptimization: 'Mantener configuración actual'
    }
  ]

  const qualityControl = [
    {
      product: 'Componente X1',
      tested: 2340,
      defects: 18,
      defectRate: 0.77,
      aiPrediction: 'Ajustar temperatura molde +2°C',
      trend: 'improving'
    },
    {
      product: 'Ensamble Y2',
      tested: 1890,
      defects: 23,
      defectRate: 1.22,
      aiPrediction: 'Calibrar torque herramientas',
      trend: 'stable'
    },
    {
      product: 'Pieza Z3',
      tested: 3450,
      defects: 12,
      defectRate: 0.35,
      aiPrediction: 'Excelente - mantener proceso',
      trend: 'improving'
    }
  ]

  const energyOptimization = [
    { hour: '6:00', consumption: 245, efficiency: 87, cost: '$89' },
    { hour: '8:00', consumption: 389, efficiency: 92, cost: '$145' },
    { hour: '10:00', consumption: 456, efficiency: 89, cost: '$178' },
    { hour: '12:00', consumption: 378, efficiency: 85, cost: '$142' },
    { hour: '14:00', consumption: 423, efficiency: 91, cost: '$165' },
    { hour: '16:00', consumption: 345, efficiency: 94, cost: '$128' }
  ]

  const criticalAlerts = [
    {
      type: 'critical',
      equipment: 'Compresor Principal',
      issue: 'Presión crítica - 15 PSI por debajo',
      time: 'Hace 30min',
      action: 'Parada inmediata requerida'
    },
    {
      type: 'warning',
      equipment: 'Motor Línea B',
      issue: 'Vibración anormal detectada',
      time: 'Hace 1h',
      action: 'Inspección programada'
    },
    {
      type: 'info',
      equipment: 'Sistema HVAC',
      issue: 'Mantenimiento preventivo debido',
      time: 'Hace 2h',
      action: 'Agendar para fin de semana'
    }
  ]

  const aiInsights = [
    {
      type: 'optimization',
      message: 'Cambiar turno de mantenimiento a 2:00 AM reducirá downtime 25%',
      confidence: 94,
      impact: 'Ahorro: $15,000/mes'
    },
    {
      type: 'prediction',
      message: 'Demanda de producto X1 aumentará 30% próximo trimestre',
      confidence: 87,
      impact: 'Preparar capacidad adicional'
    },
    {
      type: 'efficiency',
      message: 'Optimizar flujo línea C puede incrementar producción 12%',
      confidence: 91,
      impact: 'ROI: 340% anual'
    }
  ]

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
      red: 'bg-red-100 text-red-800',
      yellow: 'bg-yellow-100 text-yellow-800'
    }
    return colors[color] || colors.blue
  }

  const getIconColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500',
      yellow: 'bg-yellow-500'
    }
    return colors[color] || colors.blue
  }

  const getCriticalColor = (level) => {
    const colors = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    }
    return colors[level] || colors.medium
  }

  const getAlertColor = (type) => {
    const colors = {
      critical: 'bg-red-100',
      warning: 'bg-yellow-100',
      info: 'bg-blue-100'
    }
    return colors[type] || colors.info
  }

  const getAlertIcon = (type) => {
    if (type === 'critical') return AlertTriangle
    if (type === 'warning') return Wrench
    return Settings
  }

  const getTrendColor = (trend) => {
    const colors = {
      improving: 'text-green-600',
      stable: 'text-blue-600',
      declining: 'text-red-600'
    }
    return colors[trend] || colors.stable
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </button>
              <div className="flex items-center">
                <div className={`w-8 h-8 bg-gradient-to-br ${businessData.color} rounded-lg flex items-center justify-center mr-3`}>
                  <Factory className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{businessData.title}</h1>
                  <p className="text-sm text-gray-500">Industry 4.0 Analytics</p>
                </div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPIs Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {kpis.map((kpi, index) => {
            const IconComponent = kpi.icon
            return (
              <div key={index} className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-600">{kpi.title}</p>
                      <InfoTooltip 
                        title={kpi.tooltip.title}
                        description={kpi.tooltip.description}
                        aiMethod={kpi.tooltip.aiMethod}
                        dataSource={kpi.tooltip.dataSource}
                        updateFrequency={kpi.tooltip.updateFrequency}
                        iconColor="text-gray-400"
                      />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        {kpi.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        {kpi.description || 'vs mes anterior'}
                      </span>
                    </div>
                  </div>
                  <div className={`p-3 ${getIconColorClasses(kpi.color)} rounded-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Mantenimiento Predictivo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-gray-900">Mantenimiento Predictivo IA</h3>
                  <InfoTooltip 
                    title="Sistema de Mantenimiento Predictivo Inteligente"
                    description="Predicción de fallas en maquinaria industrial mediante análisis de sensores y algoritmos de machine learning."
                    aiMethod="Análisis de señales de sensores, detección de anomalías, predicción de vida útil, optimización de mantenimiento."
                    dataSource="Sensores IoT de vibración, temperatura, presión, histórico de mantenimientos, especificaciones de equipos."
                    updateFrequency="Monitoreo continuo con alertas predictivas en tiempo real"
                    iconColor="text-gray-400"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Brain className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-gray-600">Industrial AI</span>
                </div>
              </div>
              
              <div className="space-y-6">
                {predictiveMaintenance.map((machine, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{machine.machine}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getCriticalColor(machine.criticalLevel)}`}>
                        {machine.criticalLevel === 'high' ? 'Crítico' : machine.criticalLevel === 'medium' ? 'Medio' : 'Bajo'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-600">Salud del Equipo</p>
                        <div className="mt-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              machine.health >= 80 ? 'bg-green-500' :
                              machine.health >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${machine.health}%` }}
                          />
                        </div>
                        <p className="text-sm font-medium mt-1">{machine.health}%</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600">Eficiencia</p>
                        <div className="mt-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-blue-500"
                            style={{ width: `${machine.efficiency}%` }}
                          />
                        </div>
                        <p className="text-sm font-medium mt-1">{machine.efficiency}%</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600">Próximo Mant.</p>
                        <p className="text-lg font-bold text-orange-600">{machine.nextMaintenance}</p>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <strong>Predicción IA:</strong> {machine.predictedFailure}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Métricas de Producción */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Líneas de Producción
              </h3>
              
              <div className="space-y-4">
                {productionMetrics.map((line, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{line.line}</h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        line.efficiency >= 100 ? 'bg-green-100 text-green-800' :
                        line.efficiency >= 95 ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {line.efficiency}% eficiencia
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 mb-3 text-center">
                      <div>
                        <p className="text-sm text-gray-600">Objetivo</p>
                        <p className="text-lg font-bold text-gray-900">{line.target}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Actual</p>
                        <p className={`text-lg font-bold ${line.actual >= line.target ? 'text-green-600' : 'text-red-600'}`}>
                          {line.actual}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Calidad</p>
                        <p className="text-lg font-bold text-purple-600">{line.quality}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Downtime</p>
                        <p className="text-lg font-bold text-orange-600">{line.downtime}%</p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-700">
                        <strong>Optimización IA:</strong> {line.aiOptimization}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Alertas Críticas */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Alertas del Sistema</h3>
                <InfoTooltip 
                  title="Sistema de Alertas Industriales Inteligentes"
                  description="Alertas automáticas sobre condiciones críticas del sistema que requieren atención inmediata."
                  aiMethod="Análisis de múltiples sensores, correlación de eventos, priorización automática de alertas."
                  dataSource="Sensores de proceso, sistemas de seguridad, monitoreo de equipos, protocolos de emergencia."
                  updateFrequency="Alertas en tiempo real con clasificación automática"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {criticalAlerts.map((alert, index) => {
                  const IconComponent = getAlertIcon(alert.type)
                  return (
                    <div key={index} className="border border-gray-100 rounded-lg p-3">
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-full flex-shrink-0 ${getAlertColor(alert.type)}`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900">{alert.equipment}</h4>
                          <p className="text-sm text-gray-700 mt-1">{alert.issue}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-500">{alert.time}</span>
                            <button className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                              {alert.action}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </motion.div>

            {/* Control de Calidad */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Control de Calidad</h3>
                <InfoTooltip 
                  title="Sistema de Control de Calidad Automatizado"
                  description="Inspección automática de calidad mediante visión artificial y análisis de parámetros de producción."
                  aiMethod="Computer vision para inspección visual, análisis estadístico de calidad, detección automática de defectos."
                  dataSource="Cámaras de inspección, mediciones de calidad, especificaciones de productos, histórico de defectos."
                  updateFrequency="Inspección continua con reportes de calidad en tiempo real"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {qualityControl.map((product, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{product.product}</h4>
                      <span className={`text-sm font-medium ${getTrendColor(product.trend)}`}>
                        {product.trend === 'improving' ? '↗ Mejorando' :
                         product.trend === 'stable' ? '→ Estable' : '↘ Declinando'}
                      </span>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Probados:</span>
                        <span className="font-medium">{product.tested}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Defectos:</span>
                        <span className="font-medium">{product.defects}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tasa defectos:</span>
                        <span className={`font-medium ${product.defectRate <= 1 ? 'text-green-600' : 'text-red-600'}`}>
                          {product.defectRate}%
                        </span>
                      </div>
                    </div>
                    
                    <div className="bg-purple-50 rounded-lg p-3 mt-3">
                      <p className="text-sm text-purple-800">
                        <strong>IA:</strong> {product.aiPrediction}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Insights IA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Insights Estratégicos IA</h3>
                <InfoTooltip 
                  title="Motor de Insights Estratégicos Industriales"
                  description="Generación automática de insights estratégicos para optimizar operaciones industriales y toma de decisiones."
                  aiMethod="Análisis de big data industrial, identificación de patrones de optimización, recomendaciones estratégicas."
                  dataSource="Datos de producción, costos operativos, eficiencia energética, análisis de mercado industrial."
                  updateFrequency="Insights generados semanalmente con actualizaciones según cambios operativos"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-3">
                {aiInsights.map((insight, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-blue-100 rounded-full flex-shrink-0">
                        <Brain className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{insight.message}</p>
                        <p className="text-xs text-green-600 font-medium mt-1">{insight.impact}</p>
                        <p className="text-xs text-gray-500 mt-1">Confianza: {insight.confidence}%</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  )
} 